import { useCallback, useEffect, useRef, useState } from "react";
import { parseLibraryJSON, loadFromBlob } from "@excalidraw/excalidraw/data/blob";
import { restoreElements, restoreAppState } from "@excalidraw/excalidraw/data/restore";
import type { AppState, BinaryFileData, ExcalidrawImperativeAPI, BinaryFiles } from "@excalidraw/excalidraw/types";
import type { OrderedExcalidrawElement } from "@excalidraw/element/types";
import { getCurrentUser, type SyntrofiUser } from "./auth";
import { saveBoard } from "./saveBoard";
import { loadBoard } from "./loadBoard";
import { uploadThumbnail } from "./uploadThumbnail";
import { consumePendingBoard } from "./boardRestore";

export interface BoardState {
  // Seam: called from App.tsx onChange
  onElementsChange: (elements: readonly OrderedExcalidrawElement[], appState: AppState, files: BinaryFiles) => void;
  // Seam: called after initializeScene resolves
  onSceneInitialized: () => void;
  // Seam: passed to AppMainMenu as a single prop
  menuActions: {
    onNewBoard: () => void;
    onOpenBoardPicker: () => void;
  };
  // Data for SyntrofiUI
  boardTitle: string | null;
  dirty: boolean;
  savedAt: Date | null;
  saveType: "manual" | "auto" | null;
  boardTheme: "dark" | "light";
  currentBoardId: string | null;
  onRename: (name: string) => Promise<void>;
  onSave: () => Promise<void>;
  onSaveCopy: () => Promise<void>;
  showBoardPicker: boolean;
  onCloseBoardPicker: () => void;
  onSelectBoard: (id: string, key: string, name?: string) => Promise<void>;
  onRestoreCheckpoint: (id: string, key: string, ts: number) => Promise<void>;
  onDeletedCurrentBoard: () => void;
  onToggleShared: (id: string, shared: boolean) => Promise<void>;
  onImportFile: (file: File) => void;
  // Auth state
  currentUser: SyntrofiUser | null;
  isGuestBoard: boolean;
}

function parseHashBoard(): { id: string; key: string } | null {
  const m = window.location.hash.match(/^#json=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/);
  return m ? { id: m[1], key: m[2] } : null;
}

export function useBoardState(excalidrawAPI: ExcalidrawImperativeAPI | null): BoardState {
  const [showBoardPicker, setShowBoardPicker] = useState(false);
  const [boardTitle, setBoardTitle] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [saveType, setSaveType] = useState<"manual" | "auto" | null>(null);
  const [boardDirty, setBoardDirty] = useState(false);
  const [boardTheme, setBoardTheme] = useState<"dark" | "light">("dark");
  const [currentUser] = useState<SyntrofiUser | null>(() => getCurrentUser());
  // Pessimistic default: assume guest until onSceneInitialized confirms ownership.
  // This prevents autosave from firing before we know who owns the board.
  const [isGuestBoard, setIsGuestBoard] = useState(currentUser !== null);
  // currentBoardId as state (for UI) and refs (for callbacks, always fresh)
  const [currentBoardId, setCurrentBoardId] = useState<string | null>(
    () => parseHashBoard()?.id ?? null,
  );
  const currentBoardIdRef = useRef<string | null>(parseHashBoard()?.id ?? null);
  const currentBoardKeyRef = useRef<string | null>(parseHashBoard()?.key ?? null);
  // Always-fresh ref to excalidrawAPI for use in callbacks with stable deps
  const excalidrawAPIRef = useRef(excalidrawAPI);
  useEffect(() => {
    excalidrawAPIRef.current = excalidrawAPI;
  }, [excalidrawAPI]);

  const hasSavedRef = useRef(false);
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevElementsRef = useRef<readonly unknown[]>([]);
  // Tracks whether the current board is owned by the current user.
  // False for team boards you can edit but don't own (we don't store these
  // as "last board" in syntrofi_board localStorage).
  const isBoardOwnerRef = useRef(true);

  const setBoard = useCallback((id: string | null, key: string | null) => {
    currentBoardIdRef.current = id;
    currentBoardKeyRef.current = key;
    setCurrentBoardId(id);
  }, []);

  // Load server-provided libraries into every user's library on first API init.
  useEffect(() => {
    if (!excalidrawAPI) return;
    const SERVER_LIBRARIES = [
      "/libraries/post-it.excalidrawlib",
      "/libraries/software-architecture.excalidrawlib",
      "/libraries/UML-ER-library.excalidrawlib",
    ];
    Promise.all(
      SERVER_LIBRARIES.map((url) =>
        fetch(url)
          .then((r) => r.text())
          .then((text) => parseLibraryJSON(text, "published")),
      ),
    )
      .then((results) => {
        excalidrawAPI.updateLibrary({ libraryItems: results.flat(), merge: true });
      })
      .catch((err) => console.warn("Failed to load server libraries:", err));
  }, [excalidrawAPI]);

  // Warn before unload when the user has unsaved changes on their own board
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (boardDirty && currentUser && !isGuestBoard) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [boardDirty, currentUser, isGuestBoard]);

  const handleOpenBoard = useCallback(
    async (id: string, key: string, name?: string) => {
      const api = excalidrawAPIRef.current;
      if (!api) return;
      const imported = await loadBoard(id, key);
      api.updateScene({
        elements: restoreElements(imported.elements ?? [], null, { repairBindings: true }),
        appState: restoreAppState(imported.appState ?? {}, api.getAppState()),
      });
      if (imported.files) {
        api.addFiles(Object.values(imported.files) as BinaryFileData[]);
      }
      window.history.replaceState({}, "", `${window.location.origin}/#json=${id},${key}`);
      const meta = await fetch(`/api/v2/scenes/${id}/meta`).then((r) => r.json()).catch(() => ({}));
      const owner = meta.owner ?? null;
      const isOwner = !owner || owner === currentUser?.login;
      const isTeam = !isOwner && !!meta.shared;
      // Guest = not logged in, OR private board you don't own.
      // Team boards are editable by any logged-in member (not guest).
      const isGuest = !currentUser || (!isOwner && !isTeam);
      isBoardOwnerRef.current = isOwner;
      setIsGuestBoard(isGuest);
      setBoard(id, key);
      if (isOwner) {
        localStorage.setItem("syntrofi_board", `${id},${key}`);
      }
      setBoardTitle(name ?? meta.name ?? null);
      setSavedAt(new Date());
      setSaveType("manual");
      setBoardDirty(false);
      hasSavedRef.current = true;
      api.setToast({ message: isGuest ? "Viewing shared board" : "Board loaded", closable: true });
      setShowBoardPicker(false);
    },
    [currentUser, setBoard],
  );

  const onSceneInitialized = useCallback(() => {
    // For logged-in users, applyLastBoardToHash always stashes the target board
    // in sessionStorage and clears the hash, so Excalidraw never sees a #json=
    // hash and never shows the "replace your content" dialog. Load the board now.
    const pending = consumePendingBoard();
    if (pending) {
      handleOpenBoard(pending.id, pending.key);
      return;
    }

    const parsed = parseHashBoard();
    if (!parsed) {
      // Fresh canvas belongs to the logged-in user
      setIsGuestBoard(false);
      return;
    }

    const { id: boardId, key: boardKey } = parsed;
    setBoard(boardId, boardKey);

    fetch(`/api/v2/scenes/${boardId}/meta`)
      .then((r) => r.json())
      .then((meta) => {
        setBoardTitle(meta.name ?? null);
        setSavedAt(meta.updatedAt ? new Date(meta.updatedAt) : new Date());
        setSaveType("manual");
        hasSavedRef.current = true;
        const owner = meta.owner ?? null;
        const isOwner = !owner || owner === currentUser?.login;
        const isTeam = !isOwner && !!meta.shared;
        const isGuest = !currentUser || (!isOwner && !isTeam);
        isBoardOwnerRef.current = isOwner;
        setIsGuestBoard(isGuest);
      })
      .catch(() => {
        setIsGuestBoard(false);
      });

    // Load files from blob (upstream importFromBackend strips files; we restore them here)
    loadBoard(boardId, boardKey)
      .then((imported) => {
        const api = excalidrawAPIRef.current;
        if (api && imported.files) {
          api.addFiles(Object.values(imported.files) as BinaryFileData[]);
        }
      })
      .catch(() => {});
  }, [currentUser, setBoard, handleOpenBoard]);

  const onElementsChange = useCallback(
    (elements: readonly OrderedExcalidrawElement[], appState: AppState, files: BinaryFiles) => {
      if (appState.theme === "dark" || appState.theme === "light") {
        setBoardTheme((prev) => (prev !== appState.theme ? (appState.theme as "dark" | "light") : prev));
      }

      const elementsChanged = elements !== prevElementsRef.current;
      prevElementsRef.current = elements;

      if (hasSavedRef.current && elementsChanged) setBoardDirty(true);

      // No autosave in guest mode (user does not own this board)
      if (elementsChanged && !isGuestBoard && currentUser) {
        if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = setTimeout(async () => {
          const api = excalidrawAPIRef.current;
          if (!api) return;
          const els = api.getSceneElements();
          if (els.length === 0) return;
          try {
            const result = await saveBoard(
              els,
              api.getAppState(),
              api.getFiles(),
              {
                id: currentBoardIdRef.current ?? undefined,
                key: currentBoardKeyRef.current ?? undefined,
                name: boardTitle ?? undefined,
              },
            );
            window.history.replaceState({}, "", `${window.location.origin}/#json=${result.id},${result.key}`);
            if (isBoardOwnerRef.current) {
              localStorage.setItem("syntrofi_board", `${result.id},${result.key}`);
            }
            setBoard(result.id, result.key);
            setBoardTitle((prev) => prev ?? "Untitled Board");
            hasSavedRef.current = true;
            setSavedAt(new Date());
            setSaveType("auto");
            setBoardDirty(false);
            uploadThumbnail(result.id, api).catch(() => {});
          } catch {
            // silent - user can manually save if autosave fails
          }
        }, 10000);
      }
    },
    [boardTitle, isGuestBoard, currentUser, setBoard],
  );

  const handleSaveBoard = useCallback(async () => {
    if (!currentUser || !excalidrawAPI) return;
    const id = currentBoardIdRef.current;
    const key = currentBoardKeyRef.current;

    let saveName: string | undefined;
    if (!id) {
      saveName = boardTitle ?? window.prompt("Board name:", "Untitled Board") ?? undefined;
      if (!saveName) return;
    }

    const elements = excalidrawAPI.getSceneElements();
    const appState = excalidrawAPI.getAppState();
    const files = excalidrawAPI.getFiles();
    try {
      const result = await saveBoard(elements, appState, files, {
        id: id ?? undefined,
        key: key ?? undefined,
        name: saveName ?? boardTitle ?? undefined,
        explicit: true,
      });
      window.history.replaceState({}, "", `${window.location.origin}/#json=${result.id},${result.key}`);
      if (isBoardOwnerRef.current) {
        localStorage.setItem("syntrofi_board", `${result.id},${result.key}`);
      }
      setBoard(result.id, result.key);
      if (!id) {
        // New board was just created - current user owns it
        isBoardOwnerRef.current = true;
        localStorage.setItem("syntrofi_board", `${result.id},${result.key}`);
        setBoardTitle(saveName ?? null);
      }
      setSavedAt(new Date());
      setSaveType("manual");
      setBoardDirty(false);
      setIsGuestBoard(false);
      hasSavedRef.current = true;
      uploadThumbnail(result.id, excalidrawAPI).catch(() => {});
      excalidrawAPI.setToast({
        message: id ? "Saved" : `"${saveName}" saved`,
        closable: true,
      });
    } catch (error: any) {
      excalidrawAPI.updateScene({ appState: { errorMessage: error.message } });
    }
  }, [excalidrawAPI, boardTitle, currentUser, setBoard]);

  // Cmd+S / Ctrl+S: save to server for logged-in owners, passthrough otherwise
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s" && !e.shiftKey) {
        if (currentUser && !isGuestBoard) {
          e.preventDefault();
          e.stopPropagation();
          handleSaveBoard();
        }
      }
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [currentUser, isGuestBoard, handleSaveBoard]);

  const handleImportFile = useCallback(
    async (file: File) => {
      const api = excalidrawAPIRef.current;
      if (!api) return;
      try {
        const imported = await loadFromBlob(file, api.getAppState(), api.getSceneElements());
        api.updateScene({
          elements: restoreElements(imported.elements ?? [], null, { repairBindings: true }),
          appState: restoreAppState(imported.appState ?? {}, api.getAppState()),
        });
        if (imported.files) {
          api.addFiles(Object.values(imported.files) as BinaryFileData[]);
        }
        // Treat as a new unsaved board
        window.history.replaceState({}, "", window.location.origin);
        isBoardOwnerRef.current = true;
        setBoard(null, null);
        const guessedName = file.name.replace(/\.(excalidraw|json|png)$/i, "") || null;
        setBoardTitle(guessedName);
        setSavedAt(null);
        setSaveType(null);
        setBoardDirty(false);
        setIsGuestBoard(!currentUser);
        hasSavedRef.current = false;
      } catch {
        api.updateScene({ appState: { errorMessage: "Could not open file" } });
      }
    },
    [currentUser, setBoard],
  );

  // Save a copy to the current user's account (used in guest mode)
  const handleSaveCopy = useCallback(async () => {
    if (!currentUser || !excalidrawAPI) return;
    const suggestedName = boardTitle ? `Copy of ${boardTitle}` : "Untitled Board";
    const saveName = window.prompt("Save a copy as:", suggestedName);
    if (!saveName) return;
    const elements = excalidrawAPI.getSceneElements();
    const appState = excalidrawAPI.getAppState();
    const files = excalidrawAPI.getFiles();
    try {
      // No id/key = always POST (new board), user owns the copy
      const result = await saveBoard(elements, appState, files, { name: saveName, explicit: true });
      window.history.replaceState({}, "", `${window.location.origin}/#json=${result.id},${result.key}`);
      isBoardOwnerRef.current = true;
      localStorage.setItem("syntrofi_board", `${result.id},${result.key}`);
      setBoard(result.id, result.key);
      setBoardTitle(saveName);
      setSavedAt(new Date());
      setSaveType("manual");
      setBoardDirty(false);
      setIsGuestBoard(false);
      hasSavedRef.current = true;
      excalidrawAPI.setToast({ message: `Saved as "${saveName}"`, closable: true });
    } catch (error: any) {
      excalidrawAPI.updateScene({ appState: { errorMessage: error.message } });
    }
  }, [excalidrawAPI, boardTitle, currentUser, setBoard]);

  const handleRestoreCheckpoint = useCallback(
    async (id: string, key: string, ts: number) => {
      if (!excalidrawAPI) return;
      const imported = await loadBoard(`${id}/checkpoints/${ts}`, key);
      excalidrawAPI.updateScene({
        elements: restoreElements(imported.elements ?? [], null, { repairBindings: true }),
        appState: restoreAppState(imported.appState ?? {}, excalidrawAPI.getAppState()),
      });
      if (imported.files) {
        excalidrawAPI.addFiles(Object.values(imported.files) as BinaryFileData[]);
      }
      window.history.replaceState({}, "", `${window.location.origin}/#json=${id},${key}`);
      localStorage.setItem("syntrofi_board", `${id},${key}`);
      setBoard(id, key);
      excalidrawAPI.setToast({ message: "Checkpoint restored", closable: true });
      setShowBoardPicker(false);
    },
    [excalidrawAPI, setBoard],
  );

  const handleRename = useCallback(
    async (newName: string) => {
      setBoardTitle(newName);
      const id = currentBoardIdRef.current;
      if (!id) return;
      await fetch(`/api/v2/scenes/${id}/meta`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newName }),
      });
    },
    [],
  );

  const handleToggleShared = useCallback(async (id: string, shared: boolean) => {
    await fetch(`/api/v2/scenes/${id}/meta`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ shared }),
    });
  }, []);

  const handleNewBoard = useCallback(() => {
    if (boardDirty || boardTitle !== null) {
      if (!window.confirm("Start a new board? Unsaved changes will be lost.")) return;
    }
    if (!excalidrawAPI) return;
    excalidrawAPI.updateScene({ elements: [], appState: { collaborators: new Map() } });
    excalidrawAPI.history.clear();
    window.history.replaceState({}, "", window.location.origin);
    localStorage.removeItem("syntrofi_board");
    isBoardOwnerRef.current = true;
    setBoard(null, null);
    setBoardTitle(null);
    setSavedAt(null);
    setSaveType(null);
    setBoardDirty(false);
    setIsGuestBoard(false);
    hasSavedRef.current = false;
  }, [excalidrawAPI, boardDirty, boardTitle, setBoard]);

  const handleBoardDeleted = useCallback(() => {
    if (!excalidrawAPI) return;
    excalidrawAPI.updateScene({ elements: [], appState: { collaborators: new Map() } });
    excalidrawAPI.history.clear();
    window.history.replaceState({}, "", window.location.origin);
    localStorage.removeItem("syntrofi_board");
    isBoardOwnerRef.current = true;
    setBoard(null, null);
    setBoardTitle(null);
    setSavedAt(null);
    setSaveType(null);
    setBoardDirty(false);
    setIsGuestBoard(false);
    hasSavedRef.current = false;
  }, [excalidrawAPI, setBoard]);

  return {
    onElementsChange,
    onSceneInitialized,
    menuActions: {
      onNewBoard: handleNewBoard,
      onOpenBoardPicker: () => setShowBoardPicker(true),
    },
    boardTitle,
    dirty: boardDirty,
    savedAt,
    saveType,
    boardTheme,
    currentBoardId,
    onRename: handleRename,
    onSave: handleSaveBoard,
    onSaveCopy: handleSaveCopy,
    showBoardPicker,
    onCloseBoardPicker: () => setShowBoardPicker(false),
    onSelectBoard: handleOpenBoard,
    onRestoreCheckpoint: handleRestoreCheckpoint,
    onDeletedCurrentBoard: handleBoardDeleted,
    onToggleShared: handleToggleShared,
    onImportFile: handleImportFile,
    currentUser,
    isGuestBoard,
  };
}
